import React, { useState, useMemo } from 'react';
import DOMPurify from 'dompurify';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  ListItemIcon,
  ListItemText,
  useTheme as useThemeHook,
  useMediaQuery,
  Button,
} from '@mui/material';
import {
  Reply as ReplyIcon,
  ReplyAll as ReplyAllIcon,
  Forward as ForwardIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  MoreVert as MoreVertIcon,
  Print as PrintIcon,
  OpenInNew as OpenInNewIcon,
  Label as LabelIcon,
  Report as SpamIcon,
  DriveFileMove as MoveIcon,
  Attachment as AttachmentIcon,
  Download as DownloadIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Task as TaskIcon,
  Event as EventIcon,
  Visibility as ViewIcon,
  Inbox as InboxIcon,
  Drafts as DraftsIcon,
  Send as SendIcon,
  FolderDelete as TrashIcon,
  MarkEmailUnread as MarkUnreadIcon,
  ReportGmailerrorred as SpamFolderIcon,
  Folder as FolderIcon,
  MoreHoriz as MoreHorizIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  ExpandMore as ExpandMoreIcon,
  EditNote as EditNoteIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import { useMailStore, type Email } from '../../stores/mailStore';
import { useComposeTabsStore } from '../../stores/composeTabsStore';
import { stringToColor, getInitials } from '../../utils/helpers';
import { EmailToTaskDialog, MeetingSchedulerDialog } from '../email';
import api from '../../utils/api';
import CloseIcon from '@mui/icons-material/Close';
import AttachmentPreview from './AttachmentPreview';
import type { Attachment } from './AttachmentPreview';

interface EmailDetailViewProps {
  email: Email;
  onClose?: () => void;
}

// Helper to normalize from field which can be string or object
const getFromInfo = (from: string | { name?: string; address?: string } | undefined) => {
  if (!from) return { name: 'Unknown', address: 'unknown@unknown.com' };
  if (typeof from === 'string') {
    // Parse "Name <email>" format or just email
    const match = from.match(/^([^<]*)<([^>]+)>$/);
    if (match) {
      return { name: match[1].trim() || match[2], address: match[2] };
    }
    return { name: from.split('@')[0], address: from };
  }
  return { name: from.name || from.address || 'Unknown', address: from.address || '' };
};

// Helper to get date
const getEmailDate = (email: Email) => {
  return email.receivedAt || email.date || email.createdAt || new Date().toISOString();
};

const EmailDetailView: React.FC<EmailDetailViewProps> = ({ email, onClose }) => {
  const { markAsStarred, markAsRead, moveToFolder, markAsSpam: storeMarkAsSpam, markAsNotSpam: storeMarkAsNotSpam, deleteEmails, currentFolder: storeCurrentFolder } = useMailStore();
  const { createTab } = useComposeTabsStore();
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const [moveMenuAnchor, setMoveMenuAnchor] = useState<null | HTMLElement>(null);
  const [showFullHeader, setShowFullHeader] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [attachmentMenuAnchor, setAttachmentMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedAttachment, setSelectedAttachment] = useState<{ filename: string; contentType?: string } | null>(null);
  const [attachmentPreviewOpen, setAttachmentPreviewOpen] = useState(false);
  const [attachmentPreviewIndex, setAttachmentPreviewIndex] = useState(0);
  const [showExternalImages, setShowExternalImages] = useState(false);
  const [showDeliveryDetails, setShowDeliveryDetails] = useState(false);

  const detailTheme = useThemeHook();
  const isMobile = useMediaQuery(detailTheme.breakpoints.down('sm'));

  // Trusted senders stored in localStorage
  const trustedSenders = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('trustedImageSenders') || '[]') as string[];
    } catch { return [] as string[]; }
  }, []);
  
  const fromInfo = getFromInfo(email.from);
  const emailDate = getEmailDate(email);

  // Check if HTML contains external images (http/https src, not data: or cid:)
  const hasExternalImages = useMemo(() => {
    if (!email.htmlBody) return false;
    const imgRegex = /<img[^>]+src\s*=\s*["'](https?:\/\/[^"']+)["'][^>]*>/gi;
    return imgRegex.test(email.htmlBody);
  }, [email.htmlBody]);

  // Determine if images should be shown (trusted sender or user clicked "Display now")
  const shouldShowImages = showExternalImages || trustedSenders.includes(fromInfo.address.toLowerCase());

  // Sanitize HTML - strip external images unless allowed
  const sanitizedHtml = useMemo(() => {
    if (!email.htmlBody) return '';
    let html = email.htmlBody;
    if (hasExternalImages && !shouldShowImages) {
      // Replace external image src with empty placeholder
      html = html.replace(/<img([^>]+)src\s*=\s*["'](https?:\/\/[^"']+)["']/gi, '<img$1src="data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'20\' height=\'20\'%3E%3C/svg%3E" data-blocked-src="$2"');
    }
    return DOMPurify.sanitize(html, {
      ALLOW_UNKNOWN_PROTOCOLS: false,
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
    });
  }, [email.htmlBody, hasExternalImages, shouldShowImages]);

  const handleAlwaysDisplayFromSender = () => {
    const addr = fromInfo.address.toLowerCase();
    const current = trustedSenders.includes(addr) ? trustedSenders : [...trustedSenders, addr];
    localStorage.setItem('trustedImageSenders', JSON.stringify(current));
    setShowExternalImages(true);
  };

  // View attachment in new tab
  const handleViewAttachment = async (attachment: { filename: string; contentType?: string }) => {
    try {
      const response = await api.get(
        `/emails/${email.id}/attachments/${encodeURIComponent(attachment.filename)}`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: attachment.contentType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Revoke blob URL after a delay to prevent memory leak
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Failed to view attachment:', error);
      setSnackbarMessage('Failed to view attachment');
    }
  };

  // Download attachment with authentication
  const handleDownloadAttachment = async (attachment: { filename: string; contentType?: string }) => {
    try {
      const response = await api.get(
        `/emails/${email.id}/attachments/${encodeURIComponent(attachment.filename)}`,
        { responseType: 'blob' }
      );
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: attachment.contentType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download attachment:', error);
      setSnackbarMessage('Failed to download attachment');
    }
  };

  // Handle attachment menu
  const handleAttachmentClick = (event: React.MouseEvent<HTMLElement>, attachment: { filename: string; contentType?: string }) => {
    setAttachmentMenuAnchor(event.currentTarget);
    setSelectedAttachment(attachment);
  };

  const handleAttachmentMenuClose = () => {
    setAttachmentMenuAnchor(null);
    setSelectedAttachment(null);
  };

  // Mark as read when viewing
  React.useEffect(() => {
    if (!email.isRead) {
      markAsRead([email.id], true);
    }
  }, [email.id]);

  const getToString = () => {
    if (!email.to) return '';
    const toList = typeof email.to === 'string' ? email.to.split(',').map((e: string) => e.trim()) : email.to;
    return (toList as Array<string | { address?: string; name?: string }>).map((t) => {
      if (typeof t === 'string') return t;
      return t.name ? `${t.name} &lt;${t.address}&gt;` : (t.address || '');
    }).join(', ');
  };

  const buildQuotedBody = () => {
    const dateStr = format(new Date(emailDate), 'EEEE, MMMM d, yyyy h:mm a');
    const toStr = getToString();
    return `<br/><br/><hr style="border:none;border-top:1px solid #ccc;margin:16px 0"/><div style="color:#333;font-size:13px;"><b>From:</b> ${fromInfo.name} &lt;${fromInfo.address}&gt;<br/><b>Sent:</b> ${dateStr}<br/><b>To:</b> ${toStr}<br/><b>Subject:</b> ${email.subject || ''}</div><br/>${email.htmlBody || email.body || ''}`;
  };

  const handleReply = () => {
    createTab({
      to: [{ address: fromInfo.address, name: fromInfo.name }],
      subject: `Re: ${email.subject || ''}`,
      body: buildQuotedBody(),
    });
  };

  const handleReplyAll = () => {
    const recipients: Array<{ address: string; name?: string }> = [{ address: fromInfo.address, name: fromInfo.name }];
    const ccRecipients: Array<{ address: string; name?: string }> = [];
    if (email.to) {
      const toList = typeof email.to === 'string' ? email.to.split(',').map((e: string) => e.trim()) : email.to;
      (toList as Array<string | { address?: string; name?: string }>).forEach((to) => {
        if (typeof to === 'string') recipients.push({ address: to, name: to.split('@')[0] });
        else if (to.address) recipients.push({ address: to.address, name: to.name || to.address.split('@')[0] });
      });
    }
    if ((email as any).cc) {
      const ccList = typeof (email as any).cc === 'string' ? (email as any).cc.split(',').map((e: string) => e.trim()) : (email as any).cc;
      (ccList as Array<string | { address?: string; name?: string }>).forEach((cc) => {
        if (typeof cc === 'string') ccRecipients.push({ address: cc, name: cc.split('@')[0] });
        else if (cc.address) ccRecipients.push({ address: cc.address, name: cc.name || cc.address.split('@')[0] });
      });
    }
    createTab({
      to: recipients,
      cc: ccRecipients,
      subject: `Re: ${email.subject || ''}`,
      body: buildQuotedBody(),
    });
  };

  const handleForward = () => {
    const dateStr = format(new Date(emailDate), 'EEEE, MMMM d, yyyy h:mm a');
    const toStr = getToString();
    const body = `<br/><br/><hr style="border:none;border-top:1px solid #ccc;margin:16px 0"/><div style="color:#333;font-size:13px;"><b>From:</b> ${fromInfo.name} &lt;${fromInfo.address}&gt;<br/><b>Sent:</b> ${dateStr}<br/><b>To:</b> ${toStr}<br/><b>Subject:</b> ${email.subject || ''}</div><br/>${email.htmlBody || email.body || ''}`;
    createTab({
      to: [],
      subject: `Fwd: ${email.subject || ''}`,
      body,
    });
  };

  const handleEditAsNew = () => {
    const toRecipients: Array<{ address: string; name?: string }> = [];
    if (email.to) {
      const toList = typeof email.to === 'string' ? email.to.split(',').map((e: string) => e.trim()) : email.to;
      (toList as Array<string | { address?: string; name?: string }>).forEach((to) => {
        if (typeof to === 'string') toRecipients.push({ address: to, name: to.split('@')[0] });
        else if (to.address) toRecipients.push({ address: to.address, name: to.name || to.address.split('@')[0] });
      });
    }
    const ccRecipients: Array<{ address: string; name?: string }> = [];
    if ((email as any).cc) {
      const ccList = typeof (email as any).cc === 'string' ? (email as any).cc.split(',').map((e: string) => e.trim()) : (email as any).cc;
      (ccList as Array<string | { address?: string; name?: string }>).forEach((cc) => {
        if (typeof cc === 'string') ccRecipients.push({ address: cc, name: cc.split('@')[0] });
        else if (cc.address) ccRecipients.push({ address: cc.address, name: cc.name || cc.address.split('@')[0] });
      });
    }
    createTab({
      to: toRecipients,
      cc: ccRecipients,
      subject: email.subject || '',
      body: email.htmlBody || email.body || '',
    });
  };

  const handleCreateTask = (taskData: any) => {
    // In production, this would call the tasks API
    console.log('Creating task from email:', taskData);
    setSnackbarMessage('Task created successfully!');
    // TODO: Add to tasks store or API call
  };

  const handleScheduleMeeting = (meetingData: any) => {
    // In production, this would call the calendar API
    console.log('Scheduling meeting from email:', meetingData);
    setSnackbarMessage('Meeting scheduled and invites sent!');
    // TODO: Add to calendar store or API call
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    if (kb < 1024) return `${Math.round(kb)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  // Action handlers for toolbar
  const handleDelete = () => {
    deleteEmails([email.id]);
    setSnackbarMessage('Email moved to Trash');
    if (onClose) onClose();
  };

  const handleMarkAsSpam = () => {
    storeMarkAsSpam([email.id]);
    setSnackbarMessage('Marked as spam. Future emails from this sender will go to Spam.');
    if (onClose) onClose();
  };

  const handleMarkAsNotSpam = () => {
    storeMarkAsNotSpam([email.id]);
    setSnackbarMessage('Marked as not spam. Future emails from this sender will go to Inbox.');
    if (onClose) onClose();
  };

  const isInSpamFolder = email.folder === 'spam' || storeCurrentFolder === 'spam';

  const handleMoveToFolder = (folder: string) => {
    moveToFolder([email.id], folder);
    setMoveMenuAnchor(null);
    setSnackbarMessage(`Moved to ${folder}`);
    if (onClose) onClose();
  };

  const handleToggleStar = () => {
    markAsStarred([email.id], !email.isStarred);
  };

  const handleMarkUnread = () => {
    markAsRead([email.id], false);
    setMoreMenuAnchor(null);
    setSnackbarMessage('Marked as unread');
    if (onClose) onClose();
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html><head><title>${email.subject}</title>
        <style>body{font-family:Inter,"Segoe UI",sans-serif;padding:40px;color:#333;}</style>
        </head><body>
        <h2>${email.subject}</h2>
        <p><strong>From:</strong> ${fromInfo.name} &lt;${fromInfo.address}&gt;</p>
        <p><strong>Date:</strong> ${format(new Date(emailDate), 'PPPppp')}</p>
        <hr/>
        ${email.htmlBody || `<pre>${email.body}</pre>`}
        </body></html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // Folder config for Zoho-style folder badge
  const folderConfig: Record<string, { label: string; icon: React.ReactNode; bgcolor: string; color: string; borderColor: string }> = {
    inbox: { label: 'Inbox', icon: <InboxIcon sx={{ fontSize: 14 }} />, bgcolor: '#e8f0fe', color: '#1a73e8', borderColor: '#c6dafc' },
    sent: { label: 'Sent', icon: <SendIcon sx={{ fontSize: 14 }} />, bgcolor: '#e6f4ea', color: '#137333', borderColor: '#ceead6' },
    drafts: { label: 'Drafts', icon: <DraftsIcon sx={{ fontSize: 14 }} />, bgcolor: '#fef7e0', color: '#b06000', borderColor: '#fde293' },
    trash: { label: 'Trash', icon: <TrashIcon sx={{ fontSize: 14 }} />, bgcolor: '#fce8e6', color: '#c5221f', borderColor: '#f6aea9' },
    spam: { label: 'Spam', icon: <SpamFolderIcon sx={{ fontSize: 14 }} />, bgcolor: '#fce8e6', color: '#c5221f', borderColor: '#f6aea9' },
    archive: { label: 'Archive', icon: <ArchiveIcon sx={{ fontSize: 14 }} />, bgcolor: '#f3e8fd', color: '#7627bb', borderColor: '#e0c8f9' },
    outbox: { label: 'Outbox', icon: <SendIcon sx={{ fontSize: 14 }} />, bgcolor: '#e6f4ea', color: '#137333', borderColor: '#ceead6' },
  };
  const currentFolder = folderConfig[email.folder] || { label: email.folder || 'Inbox', icon: <FolderIcon sx={{ fontSize: 14 }} />, bgcolor: '#f1f3f4', color: '#5f6368', borderColor: '#dadce0' };

  // Zoho-style action button
  const ActionBtn = ({ icon, label, onClick, color }: { icon: React.ReactNode; label: string; onClick: () => void; color?: string }) => (
    <Tooltip title={label}>
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          borderRadius: '3px',
          color: color || '#5f6368',
          px: 1,
          py: 0.5,
          '&:hover': {
            bgcolor: '#e8f0fe',
            color: '#1561e8',
          },
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
      
      {/* ═══ TOP ACTION TOOLBAR (Zoho-style text-labeled) ═══ */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: isMobile ? 0.75 : 1.5,
          py: 0.5,
          borderBottom: '1px solid #e0e0e0',
          bgcolor: '#fff',
          minHeight: isMobile ? 44 : 40,
          gap: 0.5,
        }}
      >
        {/* Zoho-style text-labeled actions - hidden on mobile */}
        {!isMobile && [
          { icon: <EventIcon sx={{ fontSize: 16 }} />, label: 'Reminder', onClick: () => setSnackbarMessage('Reminder feature coming soon') },
          { icon: <TaskIcon sx={{ fontSize: 16 }} />, label: 'Add task', onClick: () => setTaskDialogOpen(true), hasArrow: true },
          { icon: <OpenInNewIcon sx={{ fontSize: 16 }} />, label: 'Permalink', onClick: () => { navigator.clipboard.writeText(window.location.origin + '/mail/' + email.id); setSnackbarMessage('Link copied to clipboard'); } },
          { icon: <ArchiveIcon sx={{ fontSize: 16 }} />, label: 'Snooze', onClick: () => setSnackbarMessage('Snooze feature coming soon') },
        ].map((action) => (
          <Box
            key={action.label}
            onClick={action.onClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.25,
              py: 0.5,
              borderRadius: '3px',
              cursor: 'pointer',
              color: '#5f6368',
              fontSize: '0.8rem',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              userSelect: 'none',
              '&:hover': {
                bgcolor: '#e8f0fe',
                color: '#1561e8',
              },
              transition: 'all 0.15s',
            }}
          >
            {action.icon}
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: 'inherit' }}>{action.label}</Typography>
            {action.hasArrow && <ArrowDownIcon sx={{ fontSize: 14, color: 'inherit', ml: -0.25 }} />}
          </Box>
        ))}

        <Box sx={{ flexGrow: 1 }} />

        {/* Mobile-only quick actions */}
        {isMobile && (
          <>
            <ActionBtn icon={<DeleteIcon sx={{ fontSize: 19 }} />} label="Delete" onClick={handleDelete} />
            <ActionBtn icon={<ArchiveIcon sx={{ fontSize: 19 }} />} label="Archive" onClick={() => handleMoveToFolder('archive')} />
          </>
        )}

        {/* Right-side utility icons - hidden on mobile */}
        {!isMobile && <ActionBtn icon={<PrintIcon sx={{ fontSize: 19 }} />} label="Print" onClick={handlePrint} />}
        {!isMobile && <ActionBtn icon={<OpenInNewIcon sx={{ fontSize: 19 }} />} label="Open in new window" onClick={() => {
          const newWindow = window.open('', '_blank');
          if (newWindow) {
            newWindow.document.write(`<html><head><title>${email.subject}</title><style>body{font-family:Inter,"Segoe UI",sans-serif;padding:40px;color:#333;max-width:900px;margin:0 auto;}img{max-width:100%;}</style></head><body><h2>${email.subject}</h2><p><strong>From:</strong> ${fromInfo.name} &lt;${fromInfo.address}&gt;</p><p><strong>To:</strong> ${Array.isArray(email.to) ? email.to.map((r: any) => r.name ? `${r.name} &lt;${r.address}&gt;` : r.address).join(', ') : email.to || ''}</p><p><strong>Date:</strong> ${format(new Date(emailDate), 'PPPppp')}</p><hr/>${email.htmlBody || email.body || ''}</body></html>`);
            newWindow.document.close();
          }
        }} />}
        
        {/* More menu */}
        <IconButton
          size="small"
          onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
          sx={{
            borderRadius: '3px',
            color: '#5f6368',
            px: 1,
            py: 0.5,
            '&:hover': { bgcolor: '#e8f0fe' },
          }}
        >
          <MoreVertIcon sx={{ fontSize: 19 }} />
        </IconButton>

        {onClose && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
            <Tooltip title="Close">
              <IconButton
                size="small"
                onClick={onClose}
                sx={{
                  borderRadius: '3px',
                  color: '#5f6368',
                  px: 1,
                  py: 0.5,
                  '&:hover': { bgcolor: 'rgba(211,47,47,0.08)', color: '#d32f2f' },
                }}
              >
                <CloseIcon sx={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {/* ═══ SCROLLABLE CONTENT AREA ═══ */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        
        {/* ── Subject Line + Star + Folder Badge (Zoho-style) ── */}
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          {/* Star toggle */}
          <IconButton
            size="small"
            onClick={handleToggleStar}
            sx={{
              mt: 0.15,
              p: 0.25,
              color: email.isStarred ? '#f4b400' : '#c4c7cc',
              '&:hover': { color: '#f4b400', bgcolor: 'transparent' },
            }}
          >
            {email.isStarred ? <StarIcon sx={{ fontSize: 20 }} /> : <StarBorderIcon sx={{ fontSize: 20 }} />}
          </IconButton>

          <Typography
            sx={{
              fontSize: '1.15rem',
              fontWeight: 600,
              color: '#202124',
              lineHeight: 1.4,
              fontFamily: 'Inter, "Segoe UI", sans-serif',
              flex: 1,
            }}
          >
            {email.subject || '(no subject)'}
          </Typography>
          {/* Zoho-style Folder Badge */}
          <Chip
            icon={currentFolder.icon as React.ReactElement}
            label={currentFolder.label}
            size="small"
            sx={{
              height: 24,
              fontSize: '0.72rem',
              fontWeight: 600,
              bgcolor: currentFolder.bgcolor,
              color: currentFolder.color,
              border: `1px solid ${currentFolder.borderColor}`,
              borderRadius: '4px',
              mt: 0.25,
              flexShrink: 0,
              '& .MuiChip-icon': { color: currentFolder.color, ml: 0.5 },
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        </Box>

        {/* ── Sender Section (Zoho-style) ── */}
        <Box sx={{ px: 2.5, py: 1.25, borderBottom: '1px solid #eef0f2' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            {/* Avatar */}
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: stringToColor(fromInfo.address),
                mr: 1.5,
                fontSize: '0.9rem',
                fontWeight: 600,
                mt: 0.25,
              }}
            >
              {getInitials(fromInfo.name)}
            </Avatar>

            {/* Sender Details */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#202124' }}>
                  {fromInfo.name}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#888' }}>
                  &lt;{fromInfo.address}&gt;
                </Typography>
              </Box>

              {/* To line - expandable */}
              <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mt: 0.25 }}
                onClick={() => setShowFullHeader(!showFullHeader)}
              >
                <Typography sx={{ fontSize: '0.78rem', color: '#5f6368' }}>
                  to {typeof email.to === 'string' ? email.to : (email.to?.map((t: any) => t.name || t.address).join(', ') || 'me')}
                </Typography>
                <ArrowDownIcon sx={{ fontSize: 14, color: '#5f6368', ml: 0.25, transform: showFullHeader ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </Box>

              {/* Expanded header details */}
              {showFullHeader && (
                <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f8f9fa', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
                  <Typography sx={{ fontSize: '0.78rem', color: '#5f6368', mb: 0.5 }}>
                    <strong>From:</strong> {fromInfo.name} &lt;{fromInfo.address}&gt;
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#5f6368', mb: 0.5 }}>
                    <strong>To:</strong> {typeof email.to === 'string' ? email.to : (email.to?.map((t: { name?: string; address: string }) => `${t.name || ''} <${t.address}>`).join(', '))}
                  </Typography>
                  {email.cc && (typeof email.cc === 'string' ? email.cc : email.cc.length > 0) && (
                    <Typography sx={{ fontSize: '0.78rem', color: '#5f6368', mb: 0.5 }}>
                      <strong>Cc:</strong> {typeof email.cc === 'string' ? email.cc : email.cc.map((c: { name?: string; address: string }) => `${c.name || ''} <${c.address}>`).join(', ')}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: '0.78rem', color: '#5f6368' }}>
                    <strong>Date:</strong> {format(new Date(emailDate), 'PPPppp')}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Right: Outlook-style Reply buttons + date - reply buttons hidden on mobile (shown in bottom bar) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 1, flexShrink: 0 }}>
              {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                {[
                  { label: 'Reply', icon: <ReplyIcon sx={{ fontSize: 15, color: '#8b5cf6' }} />, handler: handleReply },
                  { label: 'Reply All', icon: <ReplyAllIcon sx={{ fontSize: 15, color: '#8b5cf6' }} />, handler: handleReplyAll },
                  { label: 'Forward', icon: <ForwardIcon sx={{ fontSize: 15, color: '#3b82f6' }} />, handler: handleForward },
                ].map((action) => (
                  <Box
                    key={action.label}
                    onClick={action.handler}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1.25,
                      py: 0.35,
                      border: '1px solid #d1d5db',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      color: '#374151',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      transition: 'all 0.12s',
                      userSelect: 'none',
                      bgcolor: '#ffffff',
                      ml: '-1px',
                      '&:first-of-type': { ml: 0, borderRadius: '3px 0 0 3px' },
                      '&:last-of-type': { borderRadius: '0 3px 3px 0' },
                      '&:not(:first-of-type):not(:last-of-type)': { borderRadius: 0 },
                      '&:hover': { borderColor: '#9ca3af', bgcolor: '#f3f4f6', zIndex: 1 },
                    }}
                  >
                    {action.icon}
                    {action.label}
                  </Box>
                ))}
                <Tooltip title="More actions">
                  <IconButton
                    size="small"
                    onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                    sx={{ ml: 0.5, color: '#5f6368', p: 0.35, '&:hover': { bgcolor: '#f3f4f6' } }}
                  >
                    <MoreHorizIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              )}
              <Typography sx={{ fontSize: '0.72rem', color: '#5f6368', whiteSpace: 'nowrap', mt: 0.5, letterSpacing: '0.01em' }}>
                {format(new Date(emailDate), 'EEE, MMM d, yyyy h:mm a')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ── Delivery Status Panel (for sent emails) ── */}
        {email.folder === 'sent' && email.deliveryStatus && email.deliveryStatus !== 'none' && (
          <Box sx={{ mx: 2.5, mt: 1.5 }}>
            {/* Delivery Status Card */}
            <Box
              onClick={() => setShowDeliveryDetails(!showDeliveryDetails)}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '1px solid',
                borderColor: email.deliveryStatus === 'delivered' ? 'rgba(16, 185, 129, 0.25)'
                  : email.deliveryStatus === 'failed' ? 'rgba(239, 68, 68, 0.25)'
                  : 'rgba(245, 158, 11, 0.25)',
                background: email.deliveryStatus === 'delivered'
                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.04) 0%, rgba(6, 182, 212, 0.04) 100%)'
                  : email.deliveryStatus === 'failed'
                  ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.04) 0%, rgba(220, 38, 127, 0.04) 100%)'
                  : 'linear-gradient(135deg, rgba(245, 158, 11, 0.04) 0%, rgba(234, 179, 8, 0.04) 100%)',
                '&:hover': {
                  borderColor: email.deliveryStatus === 'delivered' ? 'rgba(16, 185, 129, 0.45)'
                    : email.deliveryStatus === 'failed' ? 'rgba(239, 68, 68, 0.45)'
                    : 'rgba(245, 158, 11, 0.45)',
                  boxShadow: email.deliveryStatus === 'delivered'
                    ? '0 2px 12px rgba(16, 185, 129, 0.1)'
                    : email.deliveryStatus === 'failed'
                    ? '0 2px 12px rgba(239, 68, 68, 0.1)'
                    : '0 2px 12px rgba(245, 158, 11, 0.1)',
                },
              }}
            >
              {/* Top accent line */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: email.deliveryStatus === 'delivered'
                  ? 'linear-gradient(90deg, #10b981, #06b6d4)'
                  : email.deliveryStatus === 'failed'
                  ? 'linear-gradient(90deg, #ef4444, #dc2680)'
                  : 'linear-gradient(90deg, #f59e0b, #eab308)',
              }} />

              <Box sx={{ px: 2, py: 1.5 }}>
                {/* Main status row */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    {/* Status icon with glow */}
                    <Box sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Box sx={{
                        position: 'absolute',
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: email.deliveryStatus === 'delivered'
                          ? 'radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, transparent 70%)'
                          : email.deliveryStatus === 'failed'
                          ? 'radial-gradient(circle, rgba(239, 68, 68, 0.15) 0%, transparent 70%)'
                          : 'radial-gradient(circle, rgba(245, 158, 11, 0.15) 0%, transparent 70%)',
                        animation: email.deliveryStatus === 'pending' ? 'pulse 2s ease-in-out infinite' : 'none',
                      }} />
                      {email.deliveryStatus === 'delivered' && (
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#10b981', position: 'relative', zIndex: 1 }} />
                      )}
                      {email.deliveryStatus === 'failed' && (
                        <ErrorIcon sx={{ fontSize: 20, color: '#ef4444', position: 'relative', zIndex: 1 }} />
                      )}
                      {(email.deliveryStatus === 'pending' || email.deliveryStatus === 'partial') && (
                        <ScheduleIcon sx={{ fontSize: 20, color: '#f59e0b', position: 'relative', zIndex: 1 }} />
                      )}
                    </Box>
                    <Box>
                      <Typography sx={{
                        fontSize: '0.82rem',
                        fontWeight: 600,
                        color: email.deliveryStatus === 'delivered' ? '#059669'
                          : email.deliveryStatus === 'failed' ? '#dc2626'
                          : '#d97706',
                        letterSpacing: '-0.01em',
                        lineHeight: 1.3,
                      }}>
                        {email.deliveryStatus === 'delivered' ? 'Delivered'
                          : email.deliveryStatus === 'failed' ? 'Delivery Failed'
                          : email.deliveryStatus === 'partial' ? 'Partially Delivered'
                          : 'Pending'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', lineHeight: 1.3, mt: 0.1 }}>
                        {email.deliveryStatus === 'delivered'
                          ? 'Email has been delivered to the recipient server'
                          : email.deliveryStatus === 'failed'
                          ? 'Email could not be delivered'
                          : 'Delivery in progress'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Right side: TLS badge + expand */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {email.deliveryDetails?.tls && (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.4,
                        px: 0.8,
                        py: 0.25,
                        borderRadius: '4px',
                        bgcolor: 'rgba(16, 185, 129, 0.08)',
                        border: '1px solid rgba(16, 185, 129, 0.2)',
                      }}>
                        <SecurityIcon sx={{ fontSize: 12, color: '#10b981' }} />
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#10b981', letterSpacing: '0.03em' }}>
                          TLS
                        </Typography>
                      </Box>
                    )}
                    <ExpandMoreIcon sx={{
                      fontSize: 18,
                      color: '#9ca3af',
                      transform: showDeliveryDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    }} />
                  </Box>
                </Box>

                {/* Expanded delivery details */}
                {showDeliveryDetails && (
                  <Box sx={{
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: '1px solid rgba(0,0,0,0.06)',
                    animation: 'fadeSlideDown 0.2s ease-out',
                    '@keyframes fadeSlideDown': {
                      from: { opacity: 0, transform: 'translateY(-8px)' },
                      to: { opacity: 1, transform: 'translateY(0)' },
                    },
                  }}>
                    {/* Delivery timeline */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {/* Timeline track */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.25 }}>
                        {/* Step 1: Sent */}
                        <Box sx={{
                          width: 8, height: 8, borderRadius: '50%',
                          bgcolor: '#10b981',
                          boxShadow: '0 0 0 3px rgba(16, 185, 129, 0.15)',
                        }} />
                        <Box sx={{
                          width: 1.5, flexGrow: 1, bgcolor: email.deliveryStatus === 'delivered' || email.deliveryStatus === 'partial' ? '#10b981' : '#e5e7eb',
                          minHeight: 20,
                        }} />
                        {/* Step 2: Delivered/Failed */}
                        <Box sx={{
                          width: 8, height: 8, borderRadius: '50%',
                          bgcolor: email.deliveryStatus === 'delivered' ? '#10b981'
                            : email.deliveryStatus === 'failed' ? '#ef4444'
                            : '#e5e7eb',
                          boxShadow: email.deliveryStatus === 'delivered'
                            ? '0 0 0 3px rgba(16, 185, 129, 0.15)'
                            : email.deliveryStatus === 'failed'
                            ? '0 0 0 3px rgba(239, 68, 68, 0.15)'
                            : 'none',
                        }} />
                      </Box>
                      {/* Timeline content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Sent step */}
                        <Box sx={{ mb: 1 }}>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#374151' }}>
                            Sent from server
                          </Typography>
                          <Typography sx={{ fontSize: '0.66rem', color: '#9ca3af' }}>
                            {email.deliveryDetails?.timestamp
                              ? format(new Date(email.deliveryDetails.timestamp), 'EEE, MMM d yyyy, h:mm:ss a')
                              : email.deliveredAt
                              ? format(new Date(email.deliveredAt), 'EEE, MMM d yyyy, h:mm:ss a')
                              : '—'}
                          </Typography>
                        </Box>
                        {/* Delivered step */}
                        <Box>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#374151' }}>
                            {email.deliveryStatus === 'delivered' ? 'Accepted by recipient server'
                              : email.deliveryStatus === 'failed' ? 'Rejected by recipient server'
                              : 'Awaiting delivery'}
                          </Typography>
                          {email.smtpResponse && (
                            <Typography sx={{
                              fontSize: '0.64rem',
                              color: '#9ca3af',
                              fontFamily: '"Fira Code", "SF Mono", monospace',
                              mt: 0.25,
                              wordBreak: 'break-all',
                            }}>
                              {email.smtpResponse}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Recipient details grid */}
                    <Box sx={{
                      mt: 1.5,
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: 1,
                    }}>
                      {/* Sender */}
                      <Box sx={{
                        p: 1,
                        borderRadius: '6px',
                        bgcolor: 'rgba(0,0,0,0.02)',
                        border: '1px solid rgba(0,0,0,0.04)',
                      }}>
                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.3 }}>
                          Sender
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Avatar sx={{ width: 18, height: 18, bgcolor: stringToColor(fromInfo.address), fontSize: '0.5rem', fontWeight: 600 }}>
                            {getInitials(fromInfo.name).charAt(0)}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {fromInfo.name}
                            </Typography>
                            <Typography sx={{ fontSize: '0.6rem', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {fromInfo.address}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Recipients */}
                      {email.deliveryDetails?.accepted && email.deliveryDetails.accepted.length > 0 && (
                        <Box sx={{
                          p: 1,
                          borderRadius: '6px',
                          bgcolor: 'rgba(16, 185, 129, 0.03)',
                          border: '1px solid rgba(16, 185, 129, 0.1)',
                        }}>
                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.3 }}>
                            Accepted ({email.deliveryDetails.accepted.length})
                          </Typography>
                          {email.deliveryDetails.accepted.map((addr: string, i: number) => (
                            <Typography key={i} sx={{ fontSize: '0.64rem', color: '#374151', lineHeight: 1.6 }}>
                              {addr}
                            </Typography>
                          ))}
                        </Box>
                      )}

                      {email.deliveryDetails?.rejected && email.deliveryDetails.rejected.length > 0 && (
                        <Box sx={{
                          p: 1,
                          borderRadius: '6px',
                          bgcolor: 'rgba(239, 68, 68, 0.03)',
                          border: '1px solid rgba(239, 68, 68, 0.1)',
                        }}>
                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.3 }}>
                            Rejected ({email.deliveryDetails.rejected.length})
                          </Typography>
                          {email.deliveryDetails.rejected.map((addr: string, i: number) => (
                            <Typography key={i} sx={{ fontSize: '0.64rem', color: '#374151', lineHeight: 1.6 }}>
                              {addr}
                            </Typography>
                          ))}
                        </Box>
                      )}

                      {/* Message size + encryption */}
                      <Box sx={{
                        p: 1,
                        borderRadius: '6px',
                        bgcolor: 'rgba(0,0,0,0.02)',
                        border: '1px solid rgba(0,0,0,0.04)',
                      }}>
                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.3 }}>
                          Details
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                          <SpeedIcon sx={{ fontSize: 11, color: '#6b7280' }} />
                          <Typography sx={{ fontSize: '0.64rem', color: '#374151' }}>
                            {email.deliveryDetails?.messageSize
                              ? `${(email.deliveryDetails.messageSize / 1024).toFixed(1)} KB`
                              : '—'}
                          </Typography>
                        </Box>
                        {email.deliveryDetails?.tls && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <SecurityIcon sx={{ fontSize: 11, color: '#10b981' }} />
                            <Typography sx={{ fontSize: '0.64rem', color: '#374151' }}>
                              TLS Encrypted
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* ── Attachments (Zoho-style cards) ── */}
        {(() => {
          // Filter out inline/signature/system attachments - only show user-attached files
          const systemMimeTypes = ['message/delivery-status', 'message/rfc822', 'text/rfc822-headers', 'application/pgp-signature', 'application/pkcs7-signature', 'application/x-pkcs7-signature', 'message/disposition-notification'];
          const realAttachments = email.attachments?.filter((att: any) => {
            // Explicitly inline disposition
            if (att.contentDisposition === 'inline') return false;
            // Has contentId — always an inline/embedded image (signatures, etc.)
            if (att.contentId) return false;
            // System MIME types (bounce reports, delivery status, signatures)
            if (systemMimeTypes.includes(att.contentType?.toLowerCase())) return false;
            // No real filename and named 'attachment' = auto-generated placeholder
            if (att.filename === 'attachment' && (!att.contentType || att.contentType.startsWith('message/') || att.contentType.startsWith('text/rfc822'))) return false;
            return true;
          }) || [];
          return realAttachments.length > 0 && (
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #eef0f2' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
              <AttachmentIcon sx={{ fontSize: 17, color: '#5f6368', transform: 'rotate(45deg)' }} />
              <Typography sx={{ fontSize: '0.8rem', color: '#5f6368', fontWeight: 500 }}>
                {realAttachments.length} Attachment{realAttachments.length > 1 ? 's' : ''}
              </Typography>
              <Typography
                component="span"
                onClick={() => realAttachments.forEach((a: any) => handleDownloadAttachment(a))}
                sx={{
                  fontSize: '0.75rem',
                  color: '#1561e8',
                  cursor: 'pointer',
                  ml: 0.5,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Download all
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {realAttachments.map((attachment: any, index: number) => (
                <Box
                  key={index}
                  onClick={(e) => handleAttachmentClick(e as any, attachment)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.25,
                    py: 0.75,
                    border: '1px solid #dadce0',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    minWidth: 180,
                    maxWidth: 280,
                    transition: 'all 0.15s',
                    '&:hover': {
                      borderColor: '#1561e8',
                      bgcolor: '#e8f0fe',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    },
                  }}
                >
                  <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '4px',
                    bgcolor: '#f1f3f4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <AttachmentIcon sx={{ fontSize: 16, color: '#5f6368' }} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{
                      fontSize: '0.78rem',
                      fontWeight: 500,
                      color: '#202124',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {attachment.filename}
                    </Typography>
                    {attachment.size && (
                      <Typography sx={{ fontSize: '0.68rem', color: '#5f6368' }}>
                        {formatFileSize(attachment.size)}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleDownloadAttachment(attachment); }}
                    sx={{ p: 0.25, color: '#5f6368', '&:hover': { color: '#1561e8' } }}
                  >
                    <DownloadIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
            
            {/* Attachment Menu */}
            <Menu
              anchorEl={attachmentMenuAnchor}
              open={Boolean(attachmentMenuAnchor)}
              onClose={handleAttachmentMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{ paper: { sx: { borderRadius: '3px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' } } }}
            >
              <MenuItem onClick={() => {
                if (selectedAttachment) {
                  // Find the index of the selected attachment in realAttachments
                  const allAtts = email.attachments || [];
                  const systemMimeTypes2 = ['message/delivery-status', 'message/rfc822', 'text/rfc822-headers', 'application/pgp-signature', 'application/pkcs7-signature', 'application/x-pkcs7-signature', 'message/disposition-notification'];
                  const filtered = allAtts.filter((att) => {
                    if (att.contentDisposition === 'inline') return false;
                    if (att.contentId) return false;
                    if (systemMimeTypes2.includes(att.contentType?.toLowerCase())) return false;
                    if (att.filename === 'attachment' && (!att.contentType || att.contentType.startsWith('message/') || att.contentType.startsWith('text/rfc822'))) return false;
                    return true;
                  });
                  const idx = filtered.findIndex((a) => a.filename === selectedAttachment.filename);
                  if (idx >= 0) {
                    setAttachmentPreviewIndex(idx);
                    setAttachmentPreviewOpen(true);
                  }
                }
                handleAttachmentMenuClose();
              }}>
                <ListItemIcon><ViewIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>View</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { if (selectedAttachment) handleDownloadAttachment(selectedAttachment); handleAttachmentMenuClose(); }}>
                <ListItemIcon><DownloadIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Download</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
          );
        })()}

        {/* ── External Images Banner (Zoho-style) ── */}
        {hasExternalImages && !shouldShowImages && (
          <Box
            sx={{
              mx: 2.5,
              mt: 1.5,
              mb: 0,
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: '#fef7e0',
              border: '1px solid #f5e6b8',
              borderRadius: '4px',
            }}
          >
            <ImageNotSupportedIcon sx={{ fontSize: 18, color: '#b8860b' }} />
            <Typography sx={{ fontSize: '0.82rem', color: '#5f4b06', flexGrow: 1 }}>
              External images are not displayed.
            </Typography>
            <Typography
              component="span"
              onClick={() => setShowExternalImages(true)}
              sx={{
                fontSize: '0.82rem',
                color: '#1561e8',
                cursor: 'pointer',
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Display now
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#999', mx: 0.5 }}>·</Typography>
            <Typography
              component="span"
              onClick={handleAlwaysDisplayFromSender}
              sx={{
                fontSize: '0.82rem',
                color: '#1561e8',
                cursor: 'pointer',
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Always display from this sender
            </Typography>
          </Box>
        )}

        {/* ── Email Body ── */}
        <Box sx={{ px: 2.5, py: 2 }}>
          {email.htmlBody ? (
            <Box
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
              sx={{
                '& img': { maxWidth: '100%', height: 'auto' },
                '& a': { color: '#1561e8', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
                '& p': { margin: '0 0 10px 0' },
                '& blockquote': {
                  borderLeft: '3px solid #ddd',
                  pl: 1.5,
                  ml: 0,
                  color: '#666',
                },
                fontSize: '0.9rem',
                lineHeight: 1.7,
                color: '#333',
                fontFamily: 'Inter, "Segoe UI", sans-serif',
                wordBreak: 'break-word',
              }}
            />
          ) : (
            <Typography
              sx={{
                whiteSpace: 'pre-wrap',
                fontSize: '0.9rem',
                lineHeight: 1.7,
                color: '#333',
                fontFamily: 'Inter, "Segoe UI", sans-serif',
                wordBreak: 'break-word',
              }}
            >
              {email.body || 'No content'}
            </Typography>
          )}
        </Box>


      </Box>

      {/* ═══ MOVE TO FOLDER MENU ═══ */}
      <Menu
        anchorEl={moveMenuAnchor}
        open={Boolean(moveMenuAnchor)}
        onClose={() => setMoveMenuAnchor(null)}
        slotProps={{ paper: { sx: { borderRadius: '3px', minWidth: 180, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' } } }}
      >
        <MenuItem onClick={() => handleMoveToFolder('inbox')}>
          <ListItemIcon><InboxIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Inbox</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMoveToFolder('drafts')}>
          <ListItemIcon><DraftsIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Drafts</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMoveToFolder('sent')}>
          <ListItemIcon><SendIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Sent</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMoveToFolder('archive')}>
          <ListItemIcon><ArchiveIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Archive</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMoveToFolder('spam')}>
          <ListItemIcon><SpamIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Spam</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMoveToFolder('trash')}>
          <ListItemIcon><TrashIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Trash</ListItemText>
        </MenuItem>
      </Menu>

      {/* ═══ MORE OPTIONS MENU ═══ */}
      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={() => setMoreMenuAnchor(null)}
        slotProps={{ paper: { sx: { borderRadius: '3px', minWidth: 200, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' } } }}
      >
        <MenuItem onClick={() => { setMoreMenuAnchor(null); handleReply(); }}>
          <ListItemIcon><ReplyIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Reply</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMoreMenuAnchor(null); handleReplyAll(); }}>
          <ListItemIcon><ReplyAllIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Reply All</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMoreMenuAnchor(null); handleForward(); }}>
          <ListItemIcon><ForwardIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Forward</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMoreMenuAnchor(null); handleEditAsNew(); }}>
          <ListItemIcon><EditNoteIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Edit as New</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setMoreMenuAnchor(null); handleToggleStar(); }}>
          <ListItemIcon>{email.isStarred ? <StarIcon sx={{ fontSize: 18, color: '#f4b400' }} /> : <StarBorderIcon sx={{ fontSize: 18 }} />}</ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>{email.isStarred ? 'Remove star' : 'Add star'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMarkUnread}>
          <ListItemIcon><MarkUnreadIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Mark as unread</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setMoreMenuAnchor(null); handleDelete(); }}>
          <ListItemIcon><DeleteIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Delete</ListItemText>
        </MenuItem>
        {isInSpamFolder ? (
          <MenuItem onClick={() => { setMoreMenuAnchor(null); handleMarkAsNotSpam(); }}>
            <ListItemIcon><InboxIcon sx={{ fontSize: 18, color: '#1a73e8' }} /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Not spam</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={() => { setMoreMenuAnchor(null); handleMarkAsSpam(); }}>
            <ListItemIcon><SpamIcon sx={{ fontSize: 18 }} /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Report spam</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => { setMoreMenuAnchor(null); moveToFolder([email.id], 'archive'); setSnackbarMessage('Archived'); if (onClose) onClose(); }}>
          <ListItemIcon><ArchiveIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Archive</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setMoreMenuAnchor(null); setMeetingDialogOpen(true); }}>
          <ListItemIcon><EventIcon sx={{ fontSize: 18, color: '#0d904f' }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Schedule Meeting</ListItemText>
        </MenuItem>
        <MenuItem onClick={(e) => { setMoreMenuAnchor(null); setMoveMenuAnchor(e.currentTarget); }}>
          <ListItemIcon><MoveIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Move to folder</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMoreMenuAnchor(null); setSnackbarMessage('Labels feature coming soon'); }}>
          <ListItemIcon><LabelIcon sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.85rem' }}>Add label</ListItemText>
        </MenuItem>
      </Menu>

      {/* Email to Task Dialog */}
      <EmailToTaskDialog
        open={taskDialogOpen}
        onClose={() => setTaskDialogOpen(false)}
        email={email}
        onCreateTask={handleCreateTask}
      />

      {/* Meeting Scheduler Dialog */}
      <MeetingSchedulerDialog
        open={meetingDialogOpen}
        onClose={() => setMeetingDialogOpen(false)}
        email={email}
        onScheduleMeeting={handleScheduleMeeting}
      />

      {/* Attachment Preview Dialog */}
      {(() => {
        const systemMimeTypes3 = ['message/delivery-status', 'message/rfc822', 'text/rfc822-headers', 'application/pgp-signature', 'application/pkcs7-signature', 'application/x-pkcs7-signature', 'message/disposition-notification'];
        const previewAttachments: Attachment[] = (email.attachments || [])
          .filter((att: any) => {
            if (att.contentDisposition === 'inline') return false;
            if (att.contentId) return false;
            if (systemMimeTypes3.includes(att.contentType?.toLowerCase())) return false;
            if (att.filename === 'attachment' && (!att.contentType || att.contentType.startsWith('message/') || att.contentType.startsWith('text/rfc822'))) return false;
            return true;
          })
          .map((att: any) => ({
            id: att.id || att.filename,
            filename: att.filename,
            mimeType: att.contentType || 'application/octet-stream',
            size: att.size || 0,
          }));
        return (
          <AttachmentPreview
            attachments={previewAttachments}
            initialIndex={attachmentPreviewIndex}
            open={attachmentPreviewOpen}
            onClose={() => setAttachmentPreviewOpen(false)}
            onDownload={(att) => handleDownloadAttachment({ filename: att.filename, contentType: att.mimeType })}
            emailId={email.id}
          />
        );
      })()}

      {/* ═══ MOBILE BOTTOM REPLY BAR (Zoho-style) ═══ */}
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderTop: '1px solid #e0e0e0',
            bgcolor: '#fff',
            flexShrink: 0,
            px: 0.5,
            py: 0.5,
            gap: 0.5,
          }}
        >
          <Button
            startIcon={<ReplyIcon sx={{ fontSize: 16 }} />}
            onClick={handleReply}
            sx={{
              flex: 1,
              textTransform: 'none',
              fontSize: '0.8rem',
              fontWeight: 500,
              color: '#5f6368',
              borderRadius: '3px',
              py: 0.75,
              '&:hover': { bgcolor: '#e8f0fe', color: '#1561e8' },
            }}
          >
            Reply
          </Button>
          <Button
            startIcon={<ReplyAllIcon sx={{ fontSize: 16 }} />}
            onClick={handleReplyAll}
            sx={{
              flex: 1,
              textTransform: 'none',
              fontSize: '0.8rem',
              fontWeight: 500,
              color: '#5f6368',
              borderRadius: '3px',
              py: 0.75,
              '&:hover': { bgcolor: '#e8f0fe', color: '#1561e8' },
            }}
          >
            Reply All
          </Button>
          <Button
            startIcon={<ForwardIcon sx={{ fontSize: 16 }} />}
            onClick={handleForward}
            sx={{
              flex: 1,
              textTransform: 'none',
              fontSize: '0.8rem',
              fontWeight: 500,
              color: '#5f6368',
              borderRadius: '3px',
              py: 0.75,
              '&:hover': { bgcolor: '#e8f0fe', color: '#1561e8' },
            }}
          >
            Forward
          </Button>
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarMessage(null)} severity="success" sx={{ width: '100%', borderRadius: '3px' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailDetailView;
